import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  catalogBanks,
  emptyBankAccount,
  endorsementFinancial,
  paymentMethods,
  policyholderFile,
  type BankAccount,
  type EndorsementDetail,
  type EndorsementRefundAccount,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

import { FinancialBankAccountFields } from '../policyholders/detail/BankAccountFields';

import { EndorsementInfo } from './EndorsementPolicyDetails';

/**
 * Secao "Dados bancarios do Tomador" do endosso de cancelamento (END-092 a END-096,
 * END-125 a END-134).
 *
 * Lista as contas que o tomador ja tem (P-TOM) e indica a conta para a restituicao. A
 * restituicao em si e regra pendente (END-005, PRC-010): a conta fica indicada, como o
 * OnPoint pede, sem que nada seja calculado. Incluir e remover conta sao as rotas do
 * tomador, com a permissao propria dos dados bancarios.
 */

function agenciaDe(conta: EndorsementRefundAccount): string {
  return [conta.agency, conta.agency_digit].filter(Boolean).join('-');
}

function contaDe(conta: EndorsementRefundAccount): string {
  return [conta.account, conta.account_digit].filter(Boolean).join('-');
}

function tipoDe(conta: EndorsementRefundAccount): string {
  return conta.account_type === 'poupanca' ? 'Poupanca' : 'Corrente';
}

export function RefundAccountSection({
  endorsement,
  editavel,
}: {
  endorsement: EndorsementDetail;
  editavel: boolean;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [incluindo, setIncluindo] = useState(false);
  const [conta, setConta] = useState<BankAccount>(emptyBankAccount);
  const [meio, setMeio] = useState('');

  const podeLerTomador = can('tomadores.tomador.view');
  const podeIncluir = can('tomadores.dados_bancarios.update');
  const podeVerMeios = can('configuracoes.meio_pagamento.view');
  const chave = ['endorsement', endorsement.id, 'refund-accounts'] as const;

  const contas = useQuery({
    queryKey: chave,
    queryFn: ({ signal }) =>
      endorsementFinancial.refundAccounts(endorsement.id, signal),
    enabled: podeLerTomador,
    retry: false,
  });
  const bancos = useQuery({
    queryKey: ['catalogs', 'bank-codes'],
    queryFn: ({ signal }) => catalogBanks.list(signal),
    staleTime: 60 * 60 * 1000,
    enabled: podeLerTomador,
  });
  const meios = useQuery({
    queryKey: ['payment-methods', 'ativos'],
    queryFn: ({ signal }) => paymentMethods.list({}, signal),
    enabled: incluindo && podeVerMeios,
  });
  const nomeDoBanco = (codigo: string) => {
    const nome = bancos.data?.find((banco) => banco.compe_code === codigo)?.name;
    return nome ? `${codigo} - ${nome}` : codigo;
  };

  const [removendo, setRemovendo] = useState<EndorsementRefundAccount | null>(null);

  function atualizar(detalhe?: EndorsementDetail) {
    if (detalhe) queryClient.setQueryData(['endorsement', endorsement.id], detalhe);
    void queryClient.invalidateQueries({ queryKey: chave });
  }

  // Incluir e remover mudam a ficha do tomador: a tela dela le de novo as contas
  // (achado B49 da revisao das ondas 5 e 6).
  function atualizarFichaDoTomador() {
    if (policyholderId) {
      void queryClient.invalidateQueries({
        queryKey: ['policyholder', policyholderId, 'bank-accounts'],
      });
    }
  }

  const escolher = useMutation({
    mutationFn: (contaId: string | null) =>
      endorsementFinancial.chooseRefundAccount(endorsement.id, contaId),
    onSuccess: (detalhe) => atualizar(detalhe),
    onError: (erro: Error) => toast.error(erro.message),
  });
  const policyholderId = contas.data?.policyholder_id ?? '';
  const incluir = useMutation({
    mutationFn: () =>
      policyholderFile.addBankAccount(policyholderId, {
        payment_method: '',
        payment_method_id: meio || null,
        bank_account: conta,
      }),
    onSuccess: () => {
      toast.success('Conta bancaria incluida.');
      setIncluindo(false);
      setConta(emptyBankAccount);
      setMeio('');
      atualizar();
      atualizarFichaDoTomador();
    },
  });
  const remover = useMutation({
    mutationFn: (contaId: string) =>
      policyholderFile.removeBankAccount(policyholderId, contaId),
    onSuccess: () => {
      toast.success('Conta bancaria removida.');
      setRemovendo(null);
      void queryClient.invalidateQueries({ queryKey: ['endorsement', endorsement.id] });
      atualizar();
      atualizarFichaDoTomador();
    },
    onError: () => toast.error('Erro ao remover conta bancaria'),
  });
  const apiError = incluir.error instanceof ApiError ? incluir.error : null;

  const escolhida = endorsement.financial?.refund_bank_account_id ?? null;
  const retrato = endorsement.financial?.refund_bank_account ?? null;
  const lista = contas.data?.accounts ?? [];

  return (
    <Card>
      <CardHeader
        title="Dados bancarios do Tomador"
        description="Selecione uma conta para recebimento da restituicao. O valor a restituir e regra pendente (END-005, PRC-010): a conta fica indicada, sem calculo."
      />
      <CardBody className="flex flex-col gap-3">
        {!podeLerTomador ? (
          <Alert tone="info" title="Sem acesso as contas do tomador">
            As contas aparecem a quem le a ficha do tomador.
            {escolhida ? ' Ha uma conta indicada para a restituicao.' : ''}
          </Alert>
        ) : null}
        {contas.isPending && podeLerTomador ? (
          <p className="text-sm text-ink-600">Carregando dados</p>
        ) : null}
        {contas.isError ? (
          <Alert tone="danger" title="Erro ao buscar contas bancarias do tomador">
            {contas.error.message}
          </Alert>
        ) : null}
        {contas.isSuccess && lista.length === 0 ? (
          <Alert tone="warning" title="Nenhum banco cadastrado">
            Voce ainda nao adicionou nenhuma conta bancaria. Cadastre uma para continuar
            com o cancelamento.
          </Alert>
        ) : null}

        {lista.length > 0 ? (
          <fieldset className="flex flex-col gap-2">
            <legend className="text-xs font-semibold text-ink-700">
              Selecione uma conta para recebimento
            </legend>
            {lista.map((item) => (
              <div
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line px-3 py-2"
              >
                <label className="flex items-center gap-2 text-sm text-ink-900">
                  <input
                    type="radio"
                    name={`conta-${endorsement.id}`}
                    checked={escolhida === item.id}
                    disabled={!editavel || escolher.isPending}
                    onChange={() => escolher.mutate(item.id)}
                    className="size-4 border-line-strong text-brand-500"
                  />
                  {nomeDoBanco(item.bank_code)} · {tipoDe(item)} · Agencia{' '}
                  {agenciaDe(item)} · Conta {contaDe(item)}
                  {item.payment_method ? ` · ${item.payment_method}` : ''}
                </label>
                {/* Remover apaga a conta da ficha do tomador: so no rascunho e com
                    confirmacao (achado B49 da revisao das ondas 5 e 6). */}
                {podeIncluir && editavel && endorsement.status === 'rascunho' ? (
                  <AcaoComIcone
                    acao="remover"
                    rotulo="Remover conta"
                    loading={remover.isPending && remover.variables === item.id}
                    onClick={() => setRemovendo(item)}
                  />
                ) : null}
              </div>
            ))}
          </fieldset>
        ) : null}

        {/* Os dados bancarios so aparecem a quem le a ficha do tomador; o servidor ja
            os omite para os demais (achado A5 da revisao das ondas 5 e 6). */}
        {retrato && podeLerTomador ? (
          <div className="grid gap-3 rounded-md border border-line bg-ink-50/50 p-3 sm:grid-cols-4">
            <EndorsementInfo rotulo="Banco" valor={nomeDoBanco(retrato.bank_code)} />
            <EndorsementInfo rotulo="Tipo de conta" valor={tipoDe(retrato)} />
            <EndorsementInfo
              rotulo="Agencia / Conta"
              valor={`${agenciaDe(retrato)} / ${contaDe(retrato)}`}
            />
            <EndorsementInfo
              rotulo="Metodo de pagamento"
              valor={retrato.payment_method}
            />
            {contas.isSuccess && !contas.data.chosen_available ? (
              <p className="text-2xs text-warning-700 sm:col-span-4">
                A conta indicada saiu do cadastro do tomador. O endosso guarda o retrato
                dela; indique outra conta, se preciso.
              </p>
            ) : null}
          </div>
        ) : null}

        {podeIncluir && editavel && contas.isSuccess ? (
          <div>
            <Button variant="secondary" size="sm" onClick={() => setIncluindo(true)}>
              Adicionar nova conta
            </Button>
          </div>
        ) : null}
      </CardBody>

      <Modal
        open={incluindo}
        onClose={() => setIncluindo(false)}
        title="Dados bancarios"
        size="md"
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            incluir.mutate();
          }}
        >
          {incluir.error ? (
            <Alert tone="danger" title="Erro ao salvar nova conta bancaria">
              {incluir.error.message}
            </Alert>
          ) : null}
          <Select
            label="Meio de pagamento"
            placeholder="Selecione o meio de pagamento"
            options={(meios.data ?? []).map((item) => ({
              value: item.id,
              label: item.label,
            }))}
            value={meio}
            disabled={(meios.data ?? []).length === 0}
            hint={
              !podeVerMeios
                ? 'Sem a permissao de ler os meios de pagamento da empresa.'
                : undefined
            }
            onChange={(event) => setMeio(event.target.value)}
          />
          <FinancialBankAccountFields
            value={conta}
            onChange={setConta}
            apiError={apiError}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setIncluindo(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={incluir.isPending}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={removendo !== null}
        onClose={() => setRemovendo(null)}
        onConfirm={() => {
          if (removendo) remover.mutate(removendo.id);
        }}
        title="Remover conta bancaria"
        message="A conta sai da ficha do tomador, e nao so deste endosso. As outras telas que usam a conta deixam de ve-la."
        confirmLabel="Remover conta"
        loading={remover.isPending}
        tone="danger"
      />
    </Card>
  );
}
